import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { useLocation, useParams } from 'react-router'

import { queries } from '../../graphql'
import { Amount, Box, Collapse, Container, FundSummary, IconButton, Icons, Link, Text, Tooltip } from '..'
import { useBranding } from '../../hooks/useBranding'

export function Header() {
  const { pathname } = useLocation()
  const defaultExpanded = /^\/funds\/\d+$/.test(pathname)
  const { matches: isWideDisplay } = window.matchMedia('(min-width: 600px)')
  const [isOpenByUser, setIsOpenByUser] = useState(null)
  const isOpen = useMemo(
    () => isOpenByUser ?? (defaultExpanded && isWideDisplay),
    [isOpenByUser, defaultExpanded, isWideDisplay]
  )

  const { fundId } = useParams()
  const { data: myFundData } = useQuery(queries.funds.myFund, {
    skip: !fundId,
    variables: { fundId: parseInt(fundId, 10) },
  })
  const fund = myFundData?.me?.fund
  const {
    userName,
    name: fundName,
    number: fundNumber,
    fundProfile,
    totalBalance,
    givingWalletBalance,
    investmentWalletBalance,
  } = fund || {}
  const name = fundProfile?.portalName ? fundProfile.portalName : userName
  const { getDebugBorder } = useBranding()

  if (!fund?.id) return null // return null if fund was not returned
  return (
    <Box
      component="header"
      sx={{
        backgroundColor: 'secondary.main',
        m: 0,
        py: 3,
        ...getDebugBorder('Header Background', '#FF0000'),
      }}
    >
      <Container maxWidth="lg" sx={{ mb: 0 }}>
        <Box sx={{ width: '100%', display: 'flex', flexWrap: 'noWrap' }}>
          <Text.H4 color="secondary.contrastText">{name}</Text.H4>
          <IconButton sx={{ color: 'secondary.contrastText' }} onClick={() => setIsOpenByUser(!isOpen)}>
            {isOpen ? <Icons.ExpandLess /> : <Icons.ExpandMore />}
          </IconButton>

          {/* commentted out until final approval of design */}
          {/* <Quicklinks fundId={fundId} /> */}
        </Box>
        <Box
          sx={{
            width: '100%',
            color: 'secondary.contrastText',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          {!isOpen && (
            <>
              <Text.Body sx={{ fontSize: '1.25rem' }}>
                <b>
                  {fundName} #{fundNumber}
                </b>
              </Text.Body>
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                <Balance
                  Icon={Icons.Account}
                  amount={totalBalance}
                  tooltip="Represents total value of Giving Wallet plus Investment Account"
                  sectionName="Balance - Total Account"
                  debugColor="#00FF00"
                />
                <Balance
                  Icon={Icons.Wallet}
                  amount={givingWalletBalance}
                  tooltip="Represents the value of funds available to give right now"
                  sectionName="Balance - Giving Wallet"
                  debugColor="#0000FF"
                />
                <Balance
                  Icon={Icons.Investments}
                  amount={investmentWalletBalance}
                  tooltip="Represents the value of funds that are 'locked' as they are in investment"
                  sectionName="Balance - Investment Account"
                  debugColor="#FFFF00"
                />
              </Box>
            </>
          )}
        </Box>
        {fund && (
          <Collapse in={isOpen} unmountOnExit>
            <Box
              sx={{
                ...getDebugBorder('Collapsed Fund Summary Section', '#FF00FF'),
              }}
            >
              <FundSummary sx={{ mb: 0 }} />
            </Box>
          </Collapse>
        )}
      </Container>
    </Box>
  )
}

function Balance({ Icon, amount, tooltip, sectionName, debugColor }) {
  const { getDebugBorder } = useBranding()

  return (
    <Tooltip title={tooltip}>
      <Text.Body
        sx={{
          fontSize: '1.25rem',
          color: 'secondary.contrastText',
          display: 'flex',
          mr: 4,
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: '4px',
          ...getDebugBorder(sectionName, debugColor),
        }}
      >
        <Icon style={{ marginRight: '.5rem', fontSize: '1rem' }} />
        <Amount value={amount} />
      </Text.Body>
    </Tooltip>
  )
}

function ContactCallOut() {
  return (
    <Text.AltBody sx={{ color: 'secondary.contrastText', textAlign: 'center', m: '16px auto 0' }}>
      We are always striving to improve. If you see anything out of place or have a question, please don&apos;t hesitate
      to contact us:{' '}
      <Link
        sx={{
          color: 'secondary.contrastText',
          textDecoration: 'none',
          '&:hover': { color: 'secondary.contrastText', textDecoration: 'underline' },
        }}
        href="mailto:support@givewise.ca"
      >
        <b>support@givewise.ca</b>
      </Link>
    </Text.AltBody>
  )
}
